import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

export const THEME_LIGHT = "light";
export const THEME_DARK = "dark";
export const THEME_AUTO = "auto";

// Per Default included in index.html
export const INITIAL_IMPORTED_THEME = THEME_LIGHT;
// ID of <Link/> Element in index.html containing the theme import
export const THEME_LINK_ID = "theme-link";

export const THEMES = {
    light: "lara-light-indigo",
    dark: "lara-dark-indigo",
} as const;

/** Theme the user can choose: a concrete theme or "auto" (follow OS preference). */
export type Theme = typeof THEME_LIGHT | typeof THEME_DARK | typeof THEME_AUTO;
/** PrimeReact theme name that is actually applied (never "auto"). */
export type AppliedTheme = (typeof THEMES)[keyof typeof THEMES];

interface ThemeState {
    currentTheme: Theme;
    appliedTheme: AppliedTheme;
    isDarkTheme: boolean;
}

const initialState: ThemeState = {
    currentTheme: INITIAL_IMPORTED_THEME,
    appliedTheme: THEMES[INITIAL_IMPORTED_THEME],
    isDarkTheme: false,
};

export const themeSlice = createSlice({
    name: "theme",
    initialState,
    reducers: {
        updateTheme: (state, action: PayloadAction<{theme: Theme; appliedTheme: AppliedTheme}>) => {
            state.currentTheme = action.payload.theme;
            state.appliedTheme = action.payload.appliedTheme;
            state.isDarkTheme = state.appliedTheme === THEMES[THEME_DARK];
        },
    },
    selectors: {
        selectCurrentTheme: (state) => state.currentTheme,
        selectAppliedTheme: (state) => state.appliedTheme,
        selectDarkTheme: (state) => state.isDarkTheme,
    },
});

export const {updateTheme} = themeSlice.actions;

export const {selectCurrentTheme, selectAppliedTheme, selectDarkTheme} = themeSlice.selectors;

export default themeSlice.reducer;
